import {Inject, Injectable, Injector} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {WindowRefService} from "../window-ref.service";
import {BehaviorSubject, Observable, of} from "rxjs";
import {SearchQueryParamsService} from "./query-params.service";
import {
    HotelToursCalendarRequest,
    HotelToursCalendarResponse,
    HotelToursFilterParams,
    HotelToursFiltersResult,
    HotelStaySearchResponse,
    HotelStaySearchResult,
    HotelToursSearchRequest,
    HotelToursSearchResponse,
    HotelToursSearchResult,
    HotelToursResultTour
} from "../../types/interfaces/hotel-tour-search";
import {switchMap} from "rxjs/operators";
import {HotelToursSearchService} from "./hotel-tours-search.service";
import {MODE} from "../../models";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";

@Injectable({providedIn: 'root'})
export class HttpHotelToursSearchService {
  private readonly hostV1: string;
  private readonly hostV2: string;
  private toursSearchService: HotelToursSearchService | null = null;
  isFindFirstToursSub = new BehaviorSubject<string>('');
  isFindFirstTours$ = this.isFindFirstToursSub.asObservable();

  constructor(
    private http: HttpClient,
    private windowRef: WindowRefService,
    private queryParams: SearchQueryParamsService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
    private window: WindowRefService,
    private injector: Injector,
  ) {
    this.hostV1 = hotelToursPageConfig.httpSearchUrl;
    this.hostV2 = hotelToursPageConfig.httpV2SearchUrl;
  }

  private getToursSearchService(): HotelToursSearchService {
    if (!this.toursSearchService) {
      this.toursSearchService = this.injector.get(HotelToursSearchService);
    }
    return this.toursSearchService;
  }

  startSearchByHotelId(request: HotelToursSearchRequest): Observable<HotelToursSearchResponse> {
    const hotelId = this.hotelToursPageConfig.hotel.hotelId;

    if (request.params.hotelsSearch) {
      return this.http.post<HotelStaySearchResponse>(
        `${this.hostV2}/web/hotels/hotel/${hotelId}`,
        this.buildHotelStaySearchRequest(request),
        {params: this.queryParams.defaultQueryParams()}
      );
    }

    return this.http.post<HotelToursSearchResponse>(
      `${this.hostV2}/web/tours/hotel/${hotelId}`,
      {...request},
      {params: this.queryParams.defaultQueryParams()}
    );
  }

  getToursByHotelId(searchID: string): Observable<HotelToursSearchResult> {
    const requestBody = {
      filters: this.getToursSearchService().getSelectedFilters()
    };

    const lastSearchParams = this.getToursSearchService().getLastSearchParams();
    if (lastSearchParams?.hotelsSearch) {
      return this.http
        .post(
          `${this.hostV2}/web/hotels/${searchID}/hotel/variants`,
          requestBody,
          {params: this.queryParams.defaultQueryParams()}
        )
        .pipe(
          switchMap((response: HotelStaySearchResult | null) => {
            if (response === null) {
              const emptyResponse: HotelToursSearchResult = {searchIsDone: true, tours: []};
              return of(emptyResponse);
            }

            return of(this.mapHotelStayResult(response));
          })
        );
    }

    return this.http
      .post(
        `${this.hostV2}/web/tours/${searchID}/hotel/variants`,
        requestBody,
        {params: this.queryParams.defaultQueryParams()}
      )
      .pipe(
        switchMap((response: HotelToursSearchResult | null) => {
          if (response === null) {
            const emptyResponse: HotelToursSearchResult = {searchIsDone: true, tours: []};
            return of(emptyResponse);
          }

          return of(response as HotelToursSearchResult);
        })
      );
  }

  getFilters(searchID: string): Observable<HotelToursFiltersResult> {
    if (this.getToursSearchService().getLastSearchParams()?.hotelsSearch) {
      const requestBody: HotelToursFilterParams = {
        filters: this.getToursSearchService().getSelectedFilters()
      };

      return this.http
        .post(
          `${this.hostV2}/web/hotels/${searchID}/hotel/filters`,
          requestBody,
          {params: this.queryParams.defaultQueryParams()}
        )
        .pipe(
          switchMap((response: HotelToursFiltersResult | null) => {
            if (response === null) {
              const emptyResponse: HotelToursFiltersResult = {filters: []};
              return of(emptyResponse);
            }
            return of(response as HotelToursFiltersResult);
          })
        );
    }

    return this.http
      .post(
        `${this.hostV2}/web/tours/${searchID}/hotel/filters`,
        {},
        {params: this.queryParams.defaultQueryParams()}
      )
      .pipe(
        switchMap((response: HotelToursFiltersResult | null) => {
          if (response === null) {
            const emptyResponse: HotelToursFiltersResult = {filters: []};
            return of(emptyResponse);
          }
          return of(response as HotelToursFiltersResult);
        })
      );
  }

  getHotelPriceCalendar(bodyParams): Observable<HotelToursCalendarResponse> {
    const requestBody = this.buildHotelPriceCalendarRequest(bodyParams);

    return this.http
      .post(
        `${this.hostV1}/web/price-calendar`,
        requestBody,
        {params: this.queryParams.defaultQueryParams()}
      )
      .pipe(
        switchMap((response: HotelToursCalendarResponse | null) => {
          if (response === null) {
            const emptyResponse: HotelToursCalendarResponse = {calendar: []};
            return of(emptyResponse);
          }
          return of(response as HotelToursCalendarResponse);
        })
      );
  }

  getHotelPriceCalendarCacheKey(bodyParams): string {
    return JSON.stringify(this.buildHotelPriceCalendarRequest(bodyParams).params);
  }

  private buildHotelPriceCalendarRequest(bodyParams): HotelToursCalendarRequest {
    const requestBody: HotelToursCalendarRequest = {
      params: {
        ...bodyParams.params,
        childAges: [...(bodyParams.params.childAges || [])],
        hotelsIds: [...(bodyParams.params.hotelsIds || [])],
        airlines: [...(bodyParams.params.airlines || [])],
      }
    };

    if (this.hotelToursPageConfig.mode === MODE.MODE_TOUR_LIST) {
      const filters = this.getToursSearchService().getSelectedFilters();

      if (filters.airlines) {
        requestBody.params.airlines = [...filters.airlines];
      }
    }

    requestBody.params.currency = this.hotelToursPageConfig.currencySignCode;

    return requestBody;
  }

  private buildHotelStaySearchRequest(request: HotelToursSearchRequest) {
    const params = request.params;

    return {
      params: {
        adults: params.adults,
        childAges: [...(params.childAges || [])],
        countryId: params.countryId,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        meals: [...(params.meals || [])],
        splitRooms: !!params.splitRooms,
      }
    };
  }

  private mapHotelStayResult(response: HotelStaySearchResult): HotelToursSearchResult {
    return {
      searchIsDone: response.searchIsDone,
      error: response.error,
      tours: (response.tours || []).map((tour, index): HotelToursResultTour => ({
        id: tour.id,
        dateFrom: tour.dateFrom,
        dateTo: tour.dateTo,
        meal: tour.meal || {name: ''},
        nights: tour.nights,
        nightsOnWay: 0,
        price: tour.price,
        rooms: tour.rooms || [],
        flights: {
          airlines: [],
          seats: [],
        },
        discount: 0,
        isBestPrice: index === 0,
        instantConfirm: !!tour.instantConfirm,
        hasSurcharges: !!tour.hasSurcharges,
      })),
    };
  }
}
